import {
    canInsertInlineIn,
    INLINE_BUTTON,
    INLINE_EMOJI,
    INLINE_EXPRESSION,
    INLINE_ICON,
    INLINE_IMAGE,
    INLINE_LINK,
    INLINE_MATH,
} from '@gitbook/doc-core';
import { isTextRangeSelection, TextRange, type Value } from '@gitbook/slate';
import type { MarkProps, Plugin } from '@gitbook/slate-react';

import { isEscape } from '../common';
import { InlineControlsPicker } from './InlineControlsPicker';

const InlineControlsArray = [
    INLINE_IMAGE,
    INLINE_EMOJI,
    INLINE_LINK,
    INLINE_MATH,
    INLINE_EXPRESSION,
    INLINE_BUTTON,
    INLINE_ICON,
] as const;

export type InlineControl = (typeof InlineControlsArray)[number];

export type InlineControls = Array<InlineControl>;

const INLINE_CONTROLS_CONTEXT_TYPE = 'inlineControlsContext';
const CAPTURE_REGEX = /(^|\s)\/$/;

/**
 * Slate plugin to detect user typing "/".
 */
export const inlineControlsPlugin: Plugin = {
    renderMark,
    decorateValue,
    onKeyDown: (event, change, editor) => {
        // If InlineControlsPicker is opened, the editor still has focus and
        // we have to prevent the 'escape' key from switching selection to a block selection
        if (getControls(editor.value) && isEscape(event)) {
            return change;
        }
        return;
    },
};

/**
 * Annotate where the user is typing "/"
 */
function decorateValue(value: Value): TextRange[] {
    const { selection } = value;
    const controls = getControls(value);
    const decorations: TextRange[] = [];

    if (isTextRangeSelection(selection)) {
        if (controls !== null) {
            decorations.push(
                TextRange.create({
                    anchorKey: selection.startKey,
                    anchorOffset: selection.startOffset - 1,
                    focusKey: selection.startKey,
                    focusOffset: selection.startOffset,
                    isBackward: selection.isBackward,
                    marks: [
                        {
                            type: INLINE_CONTROLS_CONTEXT_TYPE,
                            data: {
                                value: controls,
                            },
                        },
                    ],
                    isAtomic: true,
                })
            );
        }
    }

    return decorations;
}

function renderMark(props: MarkProps) {
    const { mark } = props;

    switch (mark.type) {
        case INLINE_CONTROLS_CONTEXT_TYPE:
            return <InlineControlsPicker {...props} />;
    }
}

function getControls(value: Value): InlineControls | null {
    if (!value.isCollapsed) {
        return null;
    }

    const { startText, startBlock, selection } = value;

    if (!isTextRangeSelection(selection)) {
        return null;
    }

    // In some cases, like if the node that was selected gets deleted,
    // `startText` can be null.
    if (!startText || !startBlock) {
        return null;
    }

    const { startOffset } = selection;
    const textBefore = startText.text.slice(0, startOffset);

    const result = CAPTURE_REGEX.exec(textBefore);

    if (result == null) {
        return null;
    }

    const inlineControls = InlineControlsArray.filter((control) =>
        canInsertInlineIn({ document: value.document, block: startBlock }, control)
    );

    return inlineControls.length === 0 ? null : inlineControls;
}
